/**
 * Shared Conversion Service
 * Used by both Trading Hub (EnhancedCryptoConversion) and Profile (Transfer) sections
 * 
 * IMPORTANT: Uses axios instead of fetch to avoid conflicts with 
 * external scripts that intercept window.fetch
 */

import axios from 'axios';

const API_URL = '';

// Create axios instance for conversion API
const conversionApi = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to all requests
conversionApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Supported currencies for conversion
export const CURRENCIES = [
  { symbol: 'USDT', name: 'Tether' },
  { symbol: 'USDC', name: 'USD Coin' },
  { symbol: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum' },
  { symbol: 'SOL', name: 'Solana' },
  { symbol: 'XRP', name: 'Ripple' },
  { symbol: 'BNB', name: 'Binance Coin' },
  { symbol: 'ADA', name: 'Cardano' },
  { symbol: 'DOGE', name: 'Dogecoin' },
  { symbol: 'GOLD', name: 'Gold' },
];

/**
 * Fetch user's wallet balance
 */
export const fetchBalance = async () => {
  try {
    const response = await conversionApi.get('/wallet/balance');
    return response.data;
  } catch (error) {
    console.error('Error fetching balance:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch balance');
  }
};

/**
 * Fetch conversion rate between two currencies
 */
export const fetchConversionRate = async (fromAsset, toAsset) => {
  try {
    const response = await conversionApi.get(`/conversion/rate/${fromAsset}/${toAsset}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching conversion rate:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch conversion rate');
  }
};

/**
 * Fetch conversion history
 */
export const fetchConversionHistory = async (limit = 20) => {
  try {
    const response = await conversionApi.get(`/conversion/history?limit=${limit}`);
    return response.data.conversions || [];
  } catch (error) {
    console.error('Error fetching conversion history:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch conversion history');
  }
};

/**
 * Perform a conversion
 */
export const performConversion = async (fromAsset, toAsset, amount) => {
  try {
    const response = await conversionApi.post('/conversion/convert', {
      from_currency: fromAsset,
      to_currency: toAsset,
      amount: parseFloat(amount)
    });
    return response.data;
  } catch (error) {
    console.error('Error converting:', error);
    throw new Error(error.response?.data?.detail || 'Conversion failed');
  }
};

/**
 * Format timestamp to readable date
 */
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format currency amount with appropriate decimals
 */
export const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') return '0';
  
  // BTC and other cryptos need more decimal places
  const decimals = ['BTC', 'ETH'].includes(currency) ? 8 : 2;
  
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};
